import { useCallback, useEffect, useRef, useState } from "react";
import type { Feature, GeoJsonProperties, Geometry } from "geojson";
import type { MapRef } from "react-map-gl/maplibre";
import { bbox } from "@turf/turf";
import { getMapFitPadding } from "@/utils/map-padding";
import { BoundaryDraw } from "../draw/boundary-draw";
import { SELECT_DRAW_MODE } from "../draw/draw-modes";

type FeatureType = Feature<Geometry, GeoJsonProperties>;

interface UseBoundaryFeaturesOptions {
  draw: BoundaryDraw;
  mapRef: React.RefObject<MapRef | null> | React.MutableRefObject<MapRef | undefined>;
  initialGeometry?: GeoJSON.FeatureCollection;
  onGeometryChange: (geometry: GeoJSON.FeatureCollection | undefined) => void;
}

export function useBoundaryFeatures({
  draw,
  mapRef,
  initialGeometry,
  onGeometryChange,
}: UseBoundaryFeaturesOptions) {
  const [features, setFeatures] = useState<GeoJSON.Feature<GeoJSON.Geometry>[]>(
    initialGeometry?.features?.map((f) => f as GeoJSON.Feature<GeoJSON.Geometry>) ?? []
  );
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);
  const [currentMode, setCurrentMode] = useState<string>(SELECT_DRAW_MODE);
  const [mapReady, setMapReady] = useState(false);
  const initialGeometryRef = useRef(initialGeometry);
  const hasLoadedInitialGeometry = useRef(false);
  const onGeometryChangeRef = useRef(onGeometryChange);
  onGeometryChangeRef.current = onGeometryChange;
  const hasSyncedFeatures = useRef(false);

  useEffect(() => {
    if (!hasSyncedFeatures.current) {
      hasSyncedFeatures.current = true;
      return;
    }
    onGeometryChangeRef.current(
      features.length === 0
        ? undefined
        : { type: "FeatureCollection", features },
    );
  }, [features]);

  const addOrUpdateFeatures = useCallback(
    (incoming: FeatureType | FeatureType[]) => {
      const incomingArray = Array.isArray(incoming) ? incoming : [incoming];
      setFeatures((prev) => {
        // Keyed lookups: an uploaded boundary can hold thousands of shapes, so a list scan is quadratic.
        const incomingById = new Map(
          incomingArray.map((f) => [f.id as string, f])
        );
        const existingIds = new Set(prev.map((f) => f.id as string));
        const newFeats = incomingArray.filter(
          (f) => !existingIds.has(f.id as string)
        );

        const result = prev.map((f) => {
          const updated = incomingById.get(f.id as string);
          if (!updated) return f;
          return {
            ...f,
            geometry: updated.geometry,
            properties: { ...updated.properties, name: f.properties?.name },
          };
        });

        return [...result, ...newFeats].map((f, i) => {
          if (!f.properties) f.properties = {};
          if (!f.properties.name) f.properties.name = `Boundary ${i + 1}`;
          return f;
        });
      });
    },
    []
  );

  const handleDelete = useCallback(
    (id: string) => {
      draw.delete([id]);
      setFeatures((prev) => prev.filter((f) => f.id !== id));
    },
    [draw]
  );

  const handleDeleteMany = useCallback(
    (ids: string[]) => {
      if (!ids.length) return;
      draw.delete(ids);
      const removed = new Set(ids);
      setFeatures((prev) => prev.filter((f) => !removed.has(f.id as string)));
    },
    [draw]
  );

  const handleRename = useCallback((id: string, name: string) => {
    setFeatures((prev) =>
      prev.map((f) =>
        f.id === id && f.properties
          ? { ...f, properties: { ...f.properties, name } }
          : f
      )
    );
  }, []);

  const replaceFeatures = useCallback(
    (newFeatures: GeoJSON.Feature<GeoJSON.Geometry>[]) => {
      draw.deleteAll();
      const named = newFeatures.map((f, i) => {
        const clone = { ...f, properties: { ...f.properties } };
        if (!clone.properties!.name) clone.properties!.name = `Boundary ${i + 1}`;
        return clone;
      });
      draw.add(named);
      setFeatures(named);
    },
    [draw],
  );

  const handleSelect = useCallback(
    (id: string) => {
      draw.select(id);
      setSelectedFeatureId(id);
    },
    [draw]
  );

  // Poll for map readiness
  useEffect(() => {
    const map = mapRef.current;
    if (map) {
      setMapReady(true);
      return;
    }
    const interval = setInterval(() => {
      if (mapRef.current) {
        setMapReady(true);
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [mapRef]);

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;

    draw.attach(map);

    const unsubscribe = [
      draw.on("create", (e: { features: Feature[] }) => {
        addOrUpdateFeatures(e.features[0]);
      }),
      draw.on("update", (e: { features: Feature[] }) => {
        addOrUpdateFeatures(e.features);
      }),
      draw.on("selectionchange", (e: { features: Feature[] }) => {
        const first = e.features[e.features.length - 1];
        setSelectedFeatureId(first ? (first.id as string) : null);
      }),
      draw.on("modechange", (e: { mode: string }) => {
        setCurrentMode(e.mode);
      }),
    ];

    const initial = initialGeometryRef.current;
    if (
      !hasLoadedInitialGeometry.current &&
      initial?.features &&
      initial.features.length > 0
    ) {
      hasLoadedInitialGeometry.current = true;
      draw.add(initial.features);
      const bounds = bbox(initial);
      mapRef.current?.fitBounds(
        [[bounds[0], bounds[1]], [bounds[2], bounds[3]]],
        { padding: getMapFitPadding(map.getContainer()) },
      );
    }

    return () => unsubscribe.forEach((off) => off());
  }, [mapReady, draw, addOrUpdateFeatures, mapRef]);

  return {
    features,
    selectedFeatureId,
    currentMode,
    setCurrentMode,
    mapReady,
    addOrUpdateFeatures,
    replaceFeatures,
    handleDelete,
    handleDeleteMany,
    handleRename,
    handleSelect,
  };
}
